import { useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  correctLine,
  getChapterDialogue,
  getCharacters,
  getReaderSpeakers,
  type ChapterDialogue,
  type DialogueLine,
  type DialogueSpeaker,
  type ReaderSpeakers,
  type Spans,
} from "../api";
import ChipRow from "../components/ChipRow";
import MutationError from "../components/MutationError";
import SheetHeader from "../components/SheetHeader";
import { useT } from "../i18n";
import { Emphasised, spanRanges } from "./emphasis";

/**
 * Who says each quote, shown in the text itself (ADR-0125): an avatar just
 * before the quote, and a sheet with the speaker's full name and the chips to
 * change it. One module for both the Player and ReadingText, so the reader can
 * use it later.
 *
 * The server places each line in the revision on screen and hands back byte
 * offsets into it — the unit the manifest's words already use — so the client
 * only inserts; it never matches text.
 */

/** Speakers is what a view needs to place and open the avatars. */
export interface Speakers {
  /** Lines to mark, by the byte they start at. Not-speech lines are left out. */
  byStart: Map<number, DialogueLine>;
  speakers: Map<string, DialogueSpeaker>;
  /** Opens the studio's sheet for a face. A reader's faces open nothing. */
  open?: (line: DialogueLine) => void;
  /**
   * A play's heads (ADR-0186): each speech's speaker name, `[start, end)` in
   * bytes, stop included, in order. Drawn as a head; never changes the text.
   */
  heads?: [number, number][];
}

/**
 * useSpeakers loads a chapter's labels placed in `revisionID` and owns the
 * sheet. `enabled` is the studio's: nothing is fetched anywhere else.
 */
export function useSpeakers(
  chapterID: string,
  bookID: string | undefined,
  revisionID: string | undefined,
  enabled: boolean,
): { speakers: Speakers | null; sheet: ReactNode } {
  const [opened, setOpened] = useState<DialogueLine | null>(null);
  const dialogue = useQuery({
    queryKey: ["dialogue", chapterID, revisionID ?? "current"],
    queryFn: () => getChapterDialogue(chapterID, revisionID),
    enabled: enabled && !!bookID,
    retry: false,
  });
  const d = dialogue.data;
  if (!enabled || !d || !bookID) return { speakers: null, sheet: null };
  const byStart = new Map<number, DialogueLine>();
  for (const line of d.lines) {
    if (line.label !== "not_speech") byStart.set(line.start_byte, line);
  }
  return {
    speakers: {
      byStart,
      speakers: new Map(d.speakers.map((s) => [s.id, s])),
      open: setOpened,
    },
    sheet: opened && (
      <SpeakerSheet
        bookID={bookID}
        chapterID={chapterID}
        line={d.lines.find((l) => l.id === opened.id) ?? opened}
        speakers={d}
        onClose={() => setOpened(null)}
      />
    ),
  };
}

const encoder = new TextEncoder();

/** utf8Length is a string's length in bytes, the unit the offsets are in. */
export function utf8Length(text: string): number {
  return encoder.encode(text).length;
}

/**
 * withSpeakers renders `text`, which starts at byte `start` of the body, with
 * an avatar inserted at every line that starts inside it, any part of a play's
 * head inside it set as a head, and any part of the body's emphasis inside it
 * drawn as emphasis (ADR-0205). With none of them inside, it is the text
 * unchanged.
 */
export function withSpeakers(
  text: string,
  start: number,
  speakers: Speakers | null | undefined,
  emphasis?: Spans,
): ReactNode {
  if (text === "") return text;
  const marks = speakers?.byStart.size
    ? markIndexes(text, start, [...speakers.byStart.keys()])
    : [];
  const heads = headRanges(text, start, speakers?.heads ?? []);
  const ems = emphasis?.length ? spanRanges(text, start, emphasis) : [];
  if (marks.length === 0 && heads.length === 0 && ems.length === 0) return text;
  // Every place the text is cut: an avatar goes in at a mark, a head or an
  // emphasis opens or closes at its ends. Marks sort before a head opening at
  // the same index, so the face sits before the name.
  const cuts = new Set<number>([0, text.length]);
  for (const [index] of marks) cuts.add(index);
  for (const [from, to] of heads) cuts.add(from).add(to);
  for (const [from, to] of ems) cuts.add(from).add(to);
  const points = [...cuts].sort((a, b) => a - b);
  const markAt = new Map(marks);
  const pieces: ReactNode[] = [];
  for (let k = 0; k < points.length - 1; k++) {
    const from = points[k]!;
    const byte = markAt.get(from);
    if (byte !== undefined && speakers) {
      pieces.push(
        <SpeakerMark
          key={`speaker-${byte}`}
          line={speakers.byStart.get(byte)!}
          speakers={speakers}
        />,
      );
    }
    const piece = text.slice(from, points[k + 1]);
    const inHead = heads.some(([a, b]) => from >= a && from < b);
    const content = ems.some(([a, b]) => from >= a && from < b) ? (
      <Emphasised key={`em-${from}`} text={piece} />
    ) : (
      piece
    );
    pieces.push(
      inHead ? (
        <span key={`head-${from}`} className="speaker-head" data-testid="speaker-head">
          {content}
        </span>
      ) : (
        content
      ),
    );
  }
  return <>{pieces}</>;
}

/**
 * headRanges is where the heads fall in `text`, which starts at byte `start`,
 * as UTF-16 `[from, to)` ranges, clipped to it. A head's line break never
 * belongs to it: the name is set, the break after it stays plain.
 */
export function headRanges(
  text: string,
  start: number,
  heads: [number, number][],
): [number, number][] {
  const end = start + utf8Length(text);
  const out: [number, number][] = [];
  for (const [a, b] of heads) {
    if (b <= start || a >= end) continue;
    let from = -1;
    let to = -1;
    let at = 0;
    let byte = start;
    for (const ch of text) {
      if (byte >= a && byte < b && ch !== "\n") {
        if (from < 0) from = at;
        to = at + ch.length;
      }
      byte += utf8Length(ch);
      at += ch.length;
    }
    if (from >= 0) out.push([from, to]);
  }
  return out;
}

/**
 * markIndexes is where each byte offset that starts inside `text` falls, as a
 * UTF-16 index into it, in order: `[index, byte]`. `start` is where `text`
 * starts in the body. The app has the same rule (mobile/lib/player/speakers.dart),
 * and both are pinned by testdata/player/speakers.json.
 */
export function markIndexes(text: string, start: number, bytes: number[]): [number, number][] {
  const end = start + utf8Length(text);
  const inside = new Set(bytes.filter((b) => b >= start && b < end));
  if (inside.size === 0) return [];
  const out: [number, number][] = [];
  let at = 0;
  let byte = start;
  for (const ch of text) {
    if (inside.has(byte)) out.push([at, byte]);
    byte += utf8Length(ch);
    at += ch.length;
  }
  return out;
}

/**
 * SpeakerMark is the avatar before a quote.
 *
 * It holds **no text and no dir** (ADR-0125, CLAUDE.md's RTL rules): a face,
 * or a drawn figure with no letters, and the name in aria-label. A letter
 * would be a strong character, and at the start of a Persian line it would
 * decide the paragraph's direction for it.
 */
export function SpeakerMark({ line, speakers }: { line: DialogueLine; speakers: Speakers }) {
  const t = useT();
  const who = line.character_id ? speakers.speakers.get(line.character_id) : undefined;
  const name =
    who?.name ?? (line.label === "unknown" ? t.dialogue.unknown : t.dialogue.someoneElse);
  const face = who?.portrait_url ? (
    <img src={who.portrait_url} alt="" className="size-full object-cover" />
  ) : (
    <Figure
      tone={line.label === "character" ? "cast" : line.label === "unknown" ? "unknown" : "other"}
    />
  );
  const shape =
    "me-1.5 -my-[0.3em] inline-grid size-[1.6em] place-items-center overflow-hidden rounded-full align-middle ring-1 ring-line";
  // A reader's face opens nothing, so it is no control: a span, which can
  // also sit inside a line's own button without nesting one in another.
  if (!speakers.open) {
    return (
      <span role="img" data-testid="speaker" aria-label={t.dialogue.saidBy(name)} className={shape}>
        {face}
      </span>
    );
  }
  const open = speakers.open;
  return (
    <button
      type="button"
      data-testid="speaker"
      data-label={line.label}
      aria-label={t.dialogue.saidBy(name)}
      onClick={(e) => {
        e.stopPropagation();
        open(line);
      }}
      className={`${shape} transition-shadow hover:ring-sage`}
    >
      {face}
    </button>
  );
}

/**
 * readerSpeakers turns a published chapter's speakers into what a view places
 * (ADR-0137): a face at every named line, a form's face for a line said in a
 * form (ADR-0173 §4: never the character's), and a play's heads (ADR-0186).
 * Quiet lines and the narrator draw nothing on the web.
 */
export function readerSpeakers(r: ReaderSpeakers): Speakers {
  const speakers = new Map<string, DialogueSpeaker>(
    r.speakers.map((s) => [
      s.id,
      { id: s.id, name: s.name, portrait_url: s.portrait?.url ?? null },
    ]),
  );
  const forms = new Map((r.forms ?? []).map((f) => [f.id, f]));
  const byStart = new Map<number, DialogueLine>();
  for (const l of r.lines) {
    // A form is drawn as a speaker of its own, keyed by its id; a form that is
    // not this character's is nobody's, and the line is the character's own.
    const form = l.form_id ? forms.get(l.form_id) : undefined;
    let who = l.character_id;
    if (form && form.character_id === l.character_id) {
      who = `form:${form.id}`;
      speakers.set(who, { id: who, name: form.name, portrait_url: form.portrait?.url ?? null });
    }
    byStart.set(l.start_byte, {
      id: `${l.start_byte}`,
      text: "",
      label: "character",
      character_id: who,
      source: "model",
      paragraph: l.paragraph,
      start_byte: l.start_byte,
      end_byte: l.end_byte,
    });
  }
  const heads: [number, number][] =
    r.kind && r.kind !== "prose"
      ? (r.units ?? [])
          .filter((u) => u.head_end_byte !== null)
          .map((u) => [u.start_byte, u.head_end_byte!])
      : [];
  return { byStart, speakers, heads };
}

/**
 * useReaderSpeakers is a published chapter's faces and heads, when the reader
 * shows who is speaking (requirement 0058). Quiet on failure: the text is the
 * thing, and none of it depends on the faces arriving.
 */
export function useReaderSpeakers(chapterID: string, enabled: boolean): Speakers | null {
  const got = useQuery({
    queryKey: ["speakers", chapterID, "reader"],
    queryFn: () => getReaderSpeakers(chapterID),
    enabled,
    retry: false,
    // The portrait URLs inside are signed for hours, not for ever.
    staleTime: 30 * 60 * 1000,
  });
  return useMemo(
    () => (enabled && got.data ? readerSpeakers(got.data) : null),
    [enabled, got.data],
  );
}

/** Figure is a drawn head and shoulders: a face without a portrait, with no letters. */
function Figure({ tone }: { tone: "cast" | "other" | "unknown" }) {
  const fill =
    tone === "cast"
      ? "bg-sage-soft text-sage-deep"
      : tone === "unknown"
        ? "bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
        : "bg-sunk text-muted";
  return (
    <span aria-hidden="true" className={`grid size-full place-items-center ${fill}`}>
      <svg viewBox="0 0 16 16" className="size-[70%]" fill="currentColor">
        <circle cx="8" cy="5.5" r="3" />
        <path d="M2.5 15c0-3.2 2.5-5.5 5.5-5.5s5.5 2.3 5.5 5.5z" />
      </svg>
    </span>
  );
}

type Choice = string; // a character id, someone_else or not_speech

/**
 * SpeakerSheet names who says a quote in full and lets the author change it
 * with chips — the cast, then someone else and not speech. The change lands on
 * the labelled row and is the author's from then on (ADR-0123 §4).
 */
function SpeakerSheet({
  bookID,
  chapterID,
  line,
  speakers,
  onClose,
}: {
  bookID: string;
  chapterID: string;
  line: DialogueLine;
  speakers: ChapterDialogue;
  onClose: () => void;
}) {
  const t = useT();
  const client = useQueryClient();
  // The whole roster, for the chips: the line may belong to somebody who has
  // no line in this chapter yet.
  const roster = useQuery({
    queryKey: ["characters", bookID, "draft"],
    queryFn: () => getCharacters(bookID),
  });
  const correct = useMutation({
    mutationFn: (choice: Choice) =>
      choice === "someone_else" || choice === "not_speech"
        ? correctLine(line.id, choice)
        : correctLine(line.id, "character", choice),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["dialogue", chapterID] });
      void client.invalidateQueries({ queryKey: ["characters", bookID, "draft"] });
      onClose();
    },
  });
  const who = line.character_id
    ? (roster.data?.characters.find((c) => c.id === line.character_id)?.name ??
      speakers.speakers.find((s) => s.id === line.character_id)?.name)
    : undefined;
  const name = who ?? (line.label === "unknown" ? t.dialogue.unknown : t.dialogue.someoneElse);
  const value: Choice =
    line.label === "character" && line.character_id ? line.character_id : line.label;

  return (
    <div
      role="dialog"
      aria-label={name}
      data-testid="speaker-sheet"
      className="fixed inset-x-0 bottom-0 z-40 max-h-[80vh] overflow-y-auto rounded-t-card border-t border-line bg-surface shadow-[0_-8px_24px_-12px_rgb(0_0_0/0.25)]"
    >
      <div className="mx-auto w-full max-w-3xl">
        <SheetHeader
          title={t.dialogue.choose}
          onClose={onClose}
          closeTestID="speaker-sheet-close"
        />
        <div className="space-y-3 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))]">
          <p className="text-lg font-semibold" data-testid="speaker-name">
            <bdi>{name}</bdi>
            {line.source === "author" && (
              <span className="ms-2 rounded-full bg-sunk px-2 py-0.5 align-middle text-xs font-normal text-muted">
                {t.dialogue.byYou}
              </span>
            )}
          </p>
          {/* The quote is authored text: its own block, and the one element
              here that decides its direction. */}
          <p dir="auto" className="text-sm leading-6 text-ink-soft">
            {line.text}
          </p>
          {roster.data && (
            <ChipRow<Choice>
              legend={t.dialogue.choose}
              disabled={correct.isPending}
              value={value}
              onChange={(choice) => correct.mutate(choice)}
              options={[
                ...roster.data.characters.map((c) => ({
                  value: c.id,
                  label: c.name,
                  testID: "speaker-choice",
                })),
                {
                  value: "someone_else",
                  label: t.dialogue.someoneElse,
                  testID: "speaker-choice-someone-else",
                },
                {
                  value: "not_speech",
                  label: t.dialogue.notSpeech,
                  testID: "speaker-choice-not-speech",
                },
              ]}
            />
          )}
          <MutationError
            testId="speaker-error"
            error={correct.error}
            fallback={t.dialogue.correctFailed}
          />
        </div>
      </div>
    </div>
  );
}
